import type { ColumnDef } from '@tanstack/react-table'
import { Badge } from '@/components/ui/badge'
import { DataTableColumnHeader } from '@/components/data-table'
import { formatDateTime } from '@/lib/format'
import type {
  AdminFeedbackKind,
  AdminFeedbackStatus,
} from '../../hooks/use-admin-feedback'

export type AdminFeedbackRow = {
  id: string
  user_id: string | null
  username: string | null
  email: string | null
  kind: AdminFeedbackKind
  message: string
  page_path: string | null
  app_version: string | null
  user_agent: string | null
  status: AdminFeedbackStatus
  resolved_by: string | null
  resolved_by_username: string | null
  resolved_at: string | null
  resolution_note: string | null
  created_at: string
}

export const FEEDBACK_STATUS_OPTIONS = [
  { label: 'Open', value: 'open' },
  { label: 'Planned', value: 'planned' },
  { label: 'Closed', value: 'closed' },
] as const

export const FEEDBACK_KIND_OPTIONS = [
  { label: 'Bug', value: 'bug' },
  { label: 'Idea', value: 'idea' },
  { label: 'Other', value: 'other' },
] as const

export const ADMIN_FEEDBACK_FILTER_KEYS = ['status', 'kind'] as const

const KIND_VARIANT: Record<
  AdminFeedbackKind,
  'warning' | 'secondary' | 'outline'
> = {
  bug: 'warning',
  idea: 'secondary',
  other: 'outline',
}

const STATUS_VARIANT: Record<
  AdminFeedbackStatus,
  'warning' | 'secondary' | 'success'
> = {
  open: 'warning',
  planned: 'secondary',
  closed: 'success',
}

const KIND_LABEL: Record<AdminFeedbackKind, string> = {
  bug: 'Bug',
  idea: 'Idea',
  other: 'Other',
}

const STATUS_LABEL: Record<AdminFeedbackStatus, string> = {
  open: 'Open',
  planned: 'Planned',
  closed: 'Closed',
}

export { KIND_LABEL, STATUS_LABEL, STATUS_VARIANT }

export function feedbackSenderLabel(row: AdminFeedbackRow): string {
  return row.username || row.email || row.user_id?.slice(0, 8) || 'Deleted user'
}

export function createAdminFeedbackColumns(): ColumnDef<
  AdminFeedbackRow,
  unknown
>[] {
  return [
    {
      id: 'kind',
      accessorKey: 'kind',
      meta: {
        label: 'Type',
        variant: 'select',
        options: [...FEEDBACK_KIND_OPTIONS],
      },
      enableSorting: false,
      enableHiding: false,
      enableColumnFilter: true,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Type" />
      ),
      cell: ({ row }) => (
        <Badge variant={KIND_VARIANT[row.original.kind]}>
          {KIND_LABEL[row.original.kind]}
        </Badge>
      ),
    },
    {
      id: 'message',
      accessorKey: 'message',
      meta: { label: 'Message' },
      enableSorting: false,
      enableHiding: false,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Message" />
      ),
      cell: ({ row }) => (
        <span className="block max-w-[28rem] truncate text-sm">
          {row.original.message}
        </span>
      ),
    },
    {
      id: 'sender',
      accessorFn: (r) => feedbackSenderLabel(r),
      meta: { label: 'From' },
      enableSorting: false,
      enableHiding: true,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="From" />
      ),
      cell: ({ row }) => (
        <span className="block max-w-[12rem] truncate text-sm text-muted-foreground">
          {feedbackSenderLabel(row.original)}
        </span>
      ),
    },
    {
      id: 'page_path',
      accessorKey: 'page_path',
      meta: { label: 'Page' },
      enableSorting: false,
      enableHiding: true,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Page" />
      ),
      cell: ({ row }) => (
        <span className="block max-w-[12rem] truncate font-mono text-[11px] text-muted-foreground">
          {row.original.page_path ?? '—'}
        </span>
      ),
    },
    {
      id: 'status',
      accessorKey: 'status',
      meta: {
        label: 'Status',
        variant: 'select',
        options: [...FEEDBACK_STATUS_OPTIONS],
      },
      enableSorting: false,
      enableHiding: true,
      enableColumnFilter: true,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Status" />
      ),
      cell: ({ row }) => <Badge variant="outline">{row.original.status}</Badge>,
    },
    {
      id: 'createdAt',
      accessorKey: 'created_at',
      meta: { label: 'Sent' },
      enableSorting: false,
      enableHiding: true,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Sent" />
      ),
      cell: ({ row }) => (
        <span className="whitespace-nowrap text-[11px] text-muted-foreground">
          {formatDateTime(row.original.created_at)}
        </span>
      ),
    },
  ]
}
